import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LANGUAGES } from "@/i18n";

export default function Navbar({ t, lang, setLang, scrollTo }) {
  const [open, setOpen] = useState(false);

  const links = [
    { id: "manifesto", label: t.nav.manifesto },
    { id: "services", label: t.nav.services },
    { id: "corridors", label: t.nav.corridors },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="navbar"
      className="fixed inset-x-0 top-0 z-40 border-b border-creamborder/70 bg-cream/80 backdrop-blur-md"
    >
      <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-3"
        >
          <img
            src="/logo.jpeg"
            alt="Hindu Kush logo"
            className="h-10 w-auto mix-blend-multiply"
          />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-forest/70 transition-colors hover:text-forest"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            data-testid="lang-switcher"
            className="flex items-center rounded-full border border-creamborder bg-creamdark/60 p-1"
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-switcher-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`rounded-full px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] transition-all duration-300 ${
                  lang === l.code
                    ? "bg-forest text-cream shadow-sm"
                    : "text-forest/60 hover:text-forest"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="group hidden items-center gap-1.5 rounded-full bg-forest px-5 py-2.5 text-xs font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-forestlight sm:flex"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-3.5 w-3.5 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-forest lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-creamborder/70 bg-cream lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-2.5 text-left font-serif text-2xl text-forest"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
